/**
 * ScheduleForDash — Wrapper da agenda para o dashboard do paciente.
 *
 * Responsabilidades:
 * - Buscar agendamentos do titular e dependentes verificados.
 * - Renderizar o componente Schedule (o Card e SectionHeader são providos pelo SortableSection).
 *
 * Não contém lógica de negócio além dos fetches já existentes.
 */
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Stack,
  SwipeableDrawer,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CalendarToday, Close, RestartAlt } from "@mui/icons-material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { Schedule } from "../../../components/schedule";
import { DashMobileSectionHeader } from "./PatientDash/components/DashMobileSectionHeader";
import { useSessionContext } from "../../../contexts/SessionContext";
import {
  getMobileSheetSlideProps,
  getMobileSheetTransitionDuration,
} from "@/components/common/mobileSheetMotion";
import {
  MOBILE_SHEET_FOOTER_BUTTON_GAP,
  mobileSheetCancelButtonSx,
  mobileSheetConfirmButtonSx,
} from "@/components/common/mobileSheetFooterButtons";
import { GetConsents } from "../../../services/consent";
import { GetAppointment } from "../../../services/fhir";
import { DateFilterType } from "../../../utils/interfaces";
import { useDatetime } from "../../../utils/useDatetime";
import { fetchDependentAppointments } from "../../../utils/patient/fetchDependentAppointments";

/** Controles de mês no header da agenda do dashboard mobile (inline — evita módulo separado). */
function DashboardMonthControls({
  dateFilter,
  setDateFilter,
  setFilterType,
}: {
  dateFilter: Dayjs;
  setDateFilter: Dispatch<SetStateAction<Dayjs>>;
  setFilterType: Dispatch<SetStateAction<DateFilterType>>;
}) {
  const theme = useTheme();
  const [openPicker, setOpenPicker] = useState(false);
  const [draftMonth, setDraftMonth] = useState(dateFilter);
  const isCurrentMonth = dateFilter.isSame(dayjs(), "month");

  useEffect(() => {
    if (openPicker) setDraftMonth(dateFilter);
  }, [openPicker, dateFilter]);

  const handleResetToCurrentMonth = () => {
    const now = dayjs();
    setDateFilter(now);
    setFilterType("month");
  };

  const handleOpenPicker = () => {
    setDraftMonth(dateFilter);
    setOpenPicker(true);
  };

  const handleConfirmMonth = () => {
    setDateFilter(draftMonth.startOf("month"));
    setFilterType("month");
    setOpenPicker(false);
  };

  return (
    <>
      <Stack
        direction="row"
        gap={0.35}
        alignItems="center"
        sx={{
          flexShrink: 0,
          py: 0.35,
          px: 0.5,
          borderRadius: 99,
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color: "text.secondary",
            fontSize: "0.6875rem",
            px: 0.5,
            textTransform: "capitalize",
          }}
        >
          {dateFilter.locale("pt-br").format("MMM YYYY")}
        </Typography>
        {!isCurrentMonth && (
          <Tooltip title="Voltar ao mês atual" arrow>
            <IconButton
              size="small"
              aria-label="Voltar ao mês atual"
              onClick={handleResetToCurrentMonth}
              sx={{
                height: 28,
                width: 28,
                bgcolor: "background.paper",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              <RestartAlt sx={{ color: "primary.main", fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          size="small"
          aria-label="Selecionar mês"
          onClick={handleOpenPicker}
          sx={{
            height: 28,
            width: 28,
            bgcolor: "background.paper",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <CalendarToday sx={{ color: "text.secondary", fontSize: 15 }} />
        </IconButton>
      </Stack>

      <SwipeableDrawer
        anchor="bottom"
        open={openPicker}
        onClose={() => setOpenPicker(false)}
        onOpen={() => undefined}
        disableSwipeToOpen
        swipeAreaWidth={0}
        transitionDuration={getMobileSheetTransitionDuration()}
        SlideProps={getMobileSheetSlideProps(theme)}
        ModalProps={{
          keepMounted: false,
          sx: { zIndex: theme.zIndex.modal + 3 },
          BackdropProps: {
            sx: { bgcolor: alpha(theme.palette.common.black, 0.45) },
            transitionDuration: getMobileSheetTransitionDuration(),
          },
        }}
        PaperProps={{
          sx: {
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "20px 20px 0 0",
            maxHeight: "88dvh",
            bgcolor: "background.paper",
            boxShadow: "0 -10px 40px rgba(0, 0, 0, 0.14)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 99,
              bgcolor: "divider",
            }}
          />
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            pb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Selecionar mês
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Escolha o mês das consultas
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Fechar"
            onClick={() => setOpenPicker(false)}
            sx={{ flexShrink: 0 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            px: 1,
            py: 0.5,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <StaticDatePicker
              displayStaticWrapperAs="mobile"
              views={["month", "year"]}
              openTo="month"
              value={draftMonth}
              onChange={(value) => {
                if (value) setDraftMonth(dayjs(value).startOf("month"));
              }}
              slotProps={{
                toolbar: { hidden: true },
                actionBar: { actions: [] },
              }}
              sx={{
                width: "100%",
                maxWidth: 360,
                "& .MuiPickersCalendarHeader-root": {
                  px: 0.5,
                },
                "& .MuiMonthCalendar-root": {
                  width: "100%",
                },
                "& .MuiYearCalendar-root": {
                  width: "100%",
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Stack
          direction="row"
          spacing={MOBILE_SHEET_FOOTER_BUTTON_GAP}
          sx={{
            px: 2,
            pt: 1,
            pb: "calc(16px + env(safe-area-inset-bottom, 0px))",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => setOpenPicker(false)}
            sx={mobileSheetCancelButtonSx}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleConfirmMonth}
            sx={mobileSheetConfirmButtonSx}
          >
            Confirmar
          </Button>
        </Stack>
      </SwipeableDrawer>
    </>
  );
}

interface ScheduleForDashProps {
  /** Reduz paddings internos quando embutido no MobileSection do dashboard */
  compact?: boolean;
  /** Mobile: slider (preview) ou lista vertical agrupada por data (Ver tudo) */
  listLayout?: "slider" | "vertical";
  /** Oculta o botão "Novo agendamento" no header compacto (dash usa o CTA superior) */
  hideNewAppointmentButton?: boolean;
  /** Informa ao pai se há consultas futuras no mês exibido (após o fetch). */
  onHasAppointmentsChange?: (hasAppointments: boolean, loading: boolean) => void;
  /** Layout do preview no dashboard mobile (título + mês no topo, Ver tudo abaixo). */
  dashboardPreview?: boolean;
  /** Lista completa (modal Ver tudo): pill de mês + bottom sheet, sem header interno da Schedule. */
  useDashboardMonthControls?: boolean;
  onViewAll?: () => void;
  /** Mês compartilhado entre preview e modal Ver tudo (opcional). */
  dateFilter?: Dayjs;
  onDateFilterChange?: (date: Dayjs) => void;
}

export function ScheduleForDash({
  compact = false,
  listLayout = "slider",
  hideNewAppointmentButton = false,
  onHasAppointmentsChange,
  dashboardPreview = false,
  useDashboardMonthControls = false,
  onViewAll,
  dateFilter: controlledDateFilter,
  onDateFilterChange,
}: ScheduleForDashProps) {
  const { inviteDialog, user, patientRelated } = useSessionContext();
  const { formatDateToISO, isDateFilterTypeDay, isSameYearAndMonthAsToday } = useDatetime;

  const [received, setReceived]                             = useState<any>([]);
  const [appointments, setAppointments]                     = useState<any>([]);
  const [appointmentsDepedents, setAppointmentsDependents]  = useState<any>([]);
  const [dependentes, setDependentes]                       = useState<any>([]);
  const [internalDateFilter, setInternalDateFilter]         = useState<Dayjs>(dayjs());
  const dateFilter = controlledDateFilter ?? internalDateFilter;
  const setDateFilter: Dispatch<SetStateAction<Dayjs>> = (value) => {
    const next = typeof value === "function" ? value(dateFilter) : value;
    onDateFilterChange?.(next);
    if (controlledDateFilter === undefined) setInternalDateFilter(next);
  };
  const [filterType, setFilterType]                         = useState<DateFilterType>("month");
  const [loading, setLoading]                               = useState<boolean>(true);
  const [refetch, setRefetch]                               = useState<boolean>(false);
  const showExternalMonthHeader = dashboardPreview || useDashboardMonthControls;

  // Calcula datas de início e fim do período conforme o tipo de filtro
  function calcDates(df: Dayjs, ft: DateFilterType) {
    const startDate =
      isDateFilterTypeDay(ft) || isSameYearAndMonthAsToday(df)
        ? formatDateToISO(df)
        : ft === "week"
          ? formatDateToISO(df.startOf("week"))
          : formatDateToISO(df.startOf("month"));
    const endDate =
      isDateFilterTypeDay(ft)
        ? formatDateToISO(df)
        : ft === "week"
          ? formatDateToISO(df.endOf("week"))
          : formatDateToISO(df.endOf("month"));
    return { startDate, endDate };
  }

  // Busca consentimentos recebidos (para determinar acesso aos dependentes)
  useEffect(() => {
    async function fetchData() {
      try {
        const receivedResponse = await GetConsents({ identifier: user.username, type: "received" });
        setReceived(
          receivedResponse?.data
            ?.filter((el: any) => el?.recurso?.id !== user.username && el?.recurso?.nome)
            ?.map((el: any) => ({ id: el?.recurso?.id, purpose: el?.contexto?.motivoAcesso })),
        );
      } catch (err) {
        console.log("ScheduleForDash fetchConsents:", err);
      }
    }
    fetchData();
  }, [user.username]);

  /**
   * Busca titular + dependentes em paralelo e limpa o bundle ao trocar período,
   * evitando consultas "fantasma" do mês anterior no botão Novo agendamento.
   */
  useEffect(() => {
    let cancelled = false;

    async function fetchAllAppointments() {
      setLoading(true);
      setAppointments(undefined);
      setAppointmentsDependents([]);

      const { startDate, endDate } = calcDates(dateFilter, filterType);
      const auxDependents = patientRelated?.filter((el: any) => el?.isVerify) ?? [];

      try {
        const titularPromise = GetAppointment({
          ehrrunner: `${user.username}`,
          startDate,
          endDate,
          status: ["booked", "pending", "proposed"],
          sort: "date",
          includeLocation: true,
        });

        const dependentsPromise =
          auxDependents.length && received?.length
            ? fetchDependentAppointments({
                dependents: auxDependents,
                receivedConsents: received,
                startDate,
                endDate,
              })
            : Promise.resolve([]);

        const [titularResponse, dependentsEntries] = await Promise.all([
          titularPromise,
          dependentsPromise,
        ]);

        if (cancelled) return;

        if (auxDependents.length) setDependentes(auxDependents);
        setAppointments(titularResponse);
        setAppointmentsDependents(dependentsEntries);
      } catch (err) {
        console.log("ScheduleForDash fetchAppointments:", err);
        if (!cancelled) {
          setAppointments(undefined);
          setAppointmentsDependents([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAllAppointments();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, filterType, user.username, patientRelated, received, refetch]);

  const bundle = {
    ...appointments,
    entry: [
      ...(appointments?.entry || []),
      ...(appointmentsDepedents || []),
    ],
  };

  useEffect(() => {
    if (!onHasAppointmentsChange || loading) return;

    const now = Date.now();
    const hasFutureAppointmentsInMonth = (bundle.entry ?? []).some((entry: any) => {
      if (entry?.resource?.resourceType !== "Appointment") return false;
      const end = entry.resource?.end;
      const start = entry.resource?.start;
      if (!start || !end || new Date(end).getTime() <= now) return false;
      return dayjs(start).isSame(dateFilter, "month");
    });

    onHasAppointmentsChange(hasFutureAppointmentsInMonth, false);
  }, [bundle.entry, dateFilter, loading, onHasAppointmentsChange]);

  if (inviteDialog) {
    return <Skeleton variant="rectangular" width="100%" height={160} sx={{ borderRadius: 2 }} />;
  }

  return (
    <Box sx={{ width: "100%", overflow: "visible" }}>
      {dashboardPreview && (
        <DashMobileSectionHeader
          title="Próximas consultas"
          subtitle="Suas consultas do mês"
          action={
            <DashboardMonthControls
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              setFilterType={setFilterType}
            />
          }
        />
      )}
      {useDashboardMonthControls && !dashboardPreview && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.25 }}>
          <DashboardMonthControls
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
            setFilterType={setFilterType}
          />
        </Stack>
      )}
      <Schedule
        relatedPatient={dependentes}
        bundle={bundle}
        dateFilter={dateFilter}
        filterType={filterType}
        setDateFilter={setDateFilter}
        setFilterType={setFilterType}
        loading={loading}
        setRefetch={setRefetch}
        refetch={refetch}
        compact={compact}
        listLayout={listLayout}
        hideNewAppointmentButton={hideNewAppointmentButton}
        hideMobileHeader={showExternalMonthHeader}
        dashboardPreview={dashboardPreview}
        showViewAllBelow={dashboardPreview && Boolean(onViewAll)}
        onViewAll={onViewAll}
      />
    </Box>
  );
}
